import { z } from 'zod';
import type { CitationRegistry } from './citations.js';

/**
 * Shape projected onto assistant-message history metadata for one tool result.
 *
 * Source of truth in CD-v3 is `casediscovery_messages_v2`: assistant rows carry
 * `tool_calls`, tool rows carry `tool_result`, and `adaptHistoryToWire` joins them
 * into this wire-friendly shape for the frontend timeline.
 */
export interface PersistedToolResult {
  toolCallId: string;
  toolName: string;
  input: Record<string, unknown>;
  result: Record<string, unknown>;
  isError: boolean;
  timestamp: string;
  /**
   * Dossier de cet appel (`metadata.calledCase` de la ligne d'outil), pour que
   * la ligne d'outil nomme un connexe après rechargement. Absent d'une ligne
   * antérieure et d'un outil qui n'agit sur aucun dossier.
   */
  caseId?: string;
}

/**
 * Structured projection of documents attached to a user message.
 *
 * The LLM replay still reads the generated suffix in `text`; this metadata is
 * for UI history and future attachment-handle restoration only.
 */
export interface PersistedUserAttachment {
  index: number;
  source?: 'workspace' | 'uploaded';
  document_id?: string;
  temp_file_id?: string;
  original_filename?: string;
}

/**
 * A dossier of the conversation's active list, as a lawyer message records it
 * (spec #2592, #2596): the `[DOSSIERS — …]` block of the next message is the
 * difference with this record. The title is the one the agent read then, so a
 * dossier removed or deleted since can still be named.
 */
/**
 * Role of a dossier in a conversation's list (spec #2592): the principal files
 * the conversation, every other dossier is related (« connexe »).
 */
export const ConversationCaseRoleSchema = z.enum(['principal', 'connexe']);
export type ConversationCaseRole = z.infer<typeof ConversationCaseRoleSchema>;

export const PersistedConversationCaseSchema = z.object({
  caseId: z.string().min(1),
  role: ConversationCaseRoleSchema,
  title: z.string(),
});
export type PersistedConversationCase = z.infer<typeof PersistedConversationCaseSchema>;

/**
 * Shape of user-message metadata in `casediscovery_messages_v2.metadata`.
 */
export interface PersistedUserMetadata {
  /**
   * Effective dossier for this turn after the backend access guard has resolved
   * the untrusted client snapshot. `null` deliberately records a validated
   * general/no-dossier turn; an absent key identifies legacy rows.
   */
  caseId?: string | null;
  /**
   * The conversation's active list (principal first) when this message was
   * saved, written by both agents (web chat and panes, Word). Absent on rows
   * from before #2596: the next message then carries no `[DOSSIERS — …]` block.
   */
  conversationCases?: PersistedConversationCase[];
  attachedFilenames?: string[];
  attachments?: PersistedUserAttachment[];
  /**
   * A document-only user turn waits for the lawyer's next instruction. The
   * backend consumes this marker exactly once so a reconnect can still read
   * the original, validated attachment handles without duplicating them in UI
   * history.
   */
  attachmentContext?: 'awaiting_instruction';
}

/**
 * Assistant-message metadata returned by CD-v3 history.
 *
 * This is a projection produced by `adaptHistoryToWire`, not the raw JSONB shape:
 * tool results are joined from v2 tool rows, and reasoning is carried from the
 * dedicated `casediscovery_messages_v2.reasoning` column.
 */
export interface PersistedAssistantMetadata {
  tools_executed: string[];
  tool_results: PersistedToolResult[];
  /**
   * Citation registry snapshot (`token → CitationTarget`) for this turn, carried
   * on the wire so the frontend can resolve `<hector-link ref="…">` tokens.
   * Accumulates across the session — the latest assistant message holds the
   * running superset; the front merges across messages defensively. Absent on
   * turns that produced no citations.
   */
  citationRegistry?: CitationRegistry;
  /**
   * Assistant reasoning/thinking for this turn — **transport-only** field added
   * by `adaptHistoryToWire` on the history endpoint so the frontend can rebuild
   * the reasoning bubble (`ThinkingGroup`) when reopening an old conversation.
   * NOT a stored metadata key: the source of truth is the dedicated
   * `casediscovery_messages_v2.reasoning` column; this just carries it on the
   * wire. Absent when the model returned no reasoning for the turn.
   */
  reasoning?: string;
}
